/**
 * The shell (design 10.1, 10.2): a white header with the mark, the client switcher, the Work/Expert switch (in a
 * client), the language switch and the account; a rail that holds the whole navigation tree (no tab strips); the
 * page; a footer with About and Privacy. At phone width the rail becomes a drawer and nothing scrolls sideways.
 * The drawer opens just below the header however many rows the header wraps to, and Escape closes it. "Ask Olivia…"
 * (H3b-6) sits in the bottom corner of every signed-in screen.
 *
 * PROVENANCE: the header and footer of C:\Gap-Assessor\src\App.tsx @ 2023d32; the rail renders
 * nav/nav-config.ts as ALEXANDER's Sidebar.tsx renders its nav-config (@ 0e341f4), with its live counters (a
 * counter that is not known shows no badge, never a 0).
 */

import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { Menu, X } from 'lucide-react'

import { LANGUAGES } from '../i18n/core'
import { useLanguage } from '../i18n/LanguageProvider'
import { api } from '../lib/api'
import { useSession } from '../lib/session'
import { clientOf } from '../lib/switcher'
import { equivalentPath, isWorkView, useWorkMode, type WorkMode } from '../lib/useWorkMode'
import { CLIENT_EXPERT_NAV, CLIENT_WORK_NAV, hrefFor, isBuilt, PORTFOLIO_NAV, visibleTo, type Counter, type NavSection } from '../nav/nav-config'
import { ClientSwitcher } from './ClientSwitcher'
import { AskOlivia } from './help/AskOlivia'
import { Mark } from './Mark'

type Counts = Partial<Record<Counter, number>>

/**
 * The rail's counters, read again when the page changes. A failed read leaves the counter unknown (no badge).
 * Each counter reads only what the person may read: their own work across their clients, or one client.
 */
function useNavCounts(client: string | null, pathname: string): Counts {
  const [counts, setCounts] = useState<{ key: string; value: Counts }>({ key: '', value: {} })
  useEffect(() => {
    let live = true
    const key = client ?? ''
    const reads: Promise<Counts>[] = client
      ? [
          api.get<{ counts: { open: number } }>(`c/${client}/actions?limit=1`).then((r) => ({ actionsOpen: r.counts.open }), () => ({})),
          api.get<{ total: number }>(`c/${client}/cycle?state=overdue&window=all&limit=1`).then((r) => ({ cycleOverdue: r.total }), () => ({})),
        ]
      : [api.get<{ total: number }>('portfolio/needs-you?limit=1').then((r) => ({ needsYou: r.total }), () => ({}))]
    void Promise.all(reads).then((parts) => {
      if (live) setCounts({ key, value: Object.assign({}, ...parts) as Counts })
    })
    return () => {
      live = false
    }
  }, [client, pathname])
  // Counters of another client are never shown under this one.
  return counts.key === (client ?? '') ? counts.value : {}
}

function Rail({ sections, client, counts, onNavigate }: { sections: readonly NavSection[]; client: string | null; counts: Counts; onNavigate: () => void }) {
  const { t } = useLanguage()
  const { person } = useSession()
  // Horizon's rule: an entry that opens an empty page is a promise not kept. What is not built yet is gathered in one
  // closed group at the end, by name, with no build phase (expert review FAM-04).
  const later = sections.flatMap((s) => s.items.filter((i) => visibleTo(i, person.firmRoles) && !isBuilt(i)))
  return (
    <nav aria-label={t('shell.mainNav')} className="space-y-5 p-4">
      {sections.map((s) => {
        const items = s.items.filter((i) => visibleTo(i, person.firmRoles) && isBuilt(i))
        if (items.length === 0) return null
        return (
          <div key={s.id}>
            <h2 className="mb-1 px-2 text-sm font-semibold uppercase tracking-wide text-ol-text-3">{t(s.labelKey)}</h2>
            <ul className="space-y-0.5">
              {items.map((i) => {
                const n = i.counter ? counts[i.counter] : undefined
                return (
                  <li key={i.id}>
                    <NavLink
                      to={hrefFor(i, client)}
                      end
                      onClick={onNavigate}
                      title={t(i.answersKey)}
                      className={({ isActive }) =>
                        `flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-[15px] ${
                          isActive ? 'bg-ol-action-soft font-medium text-ol-action-dark' : 'text-ol-text hover:bg-ol-page'
                        }`
                      }
                    >
                      <span>{t(i.labelKey)}</span>
                      {n !== undefined && n > 0 && (
                        <span className="rounded-full bg-ol-page px-2 text-sm tabular text-ol-text-2" aria-label={t(`nav.counter.${i.counter ?? 'needsYou'}`, { n })}>
                          {n}
                        </span>
                      )}
                    </NavLink>
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}
      {later.length > 0 && (
        <details>
          <summary className="cursor-pointer px-2 text-sm font-semibold uppercase tracking-wide text-ol-text-3">{t('later.group', { n: later.length })}</summary>
          <ul className="mt-1 space-y-0.5">
            {later.map((i) => (
              <li key={i.id}>
                <NavLink
                  to={hrefFor(i, client)}
                  end
                  onClick={onNavigate}
                  title={t(i.answersKey)}
                  className={({ isActive }) => `block rounded-md px-2 py-1.5 text-[15px] ${isActive ? 'bg-ol-action-soft font-medium text-ol-action-dark' : 'text-ol-text-2 hover:bg-ol-page'}`}
                >
                  {t(i.labelKey)}
                </NavLink>
              </li>
            ))}
          </ul>
        </details>
      )}
    </nav>
  )
}

/** The height of an element, kept current as it wraps (a ResizeObserver where there is one). */
function useHeight(ref: RefObject<HTMLElement | null>): number {
  const [height, setHeight] = useState(52)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => setHeight(Math.round(el.getBoundingClientRect().height) || 52)
    measure()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref])
  return height
}

export function Shell({ children }: { children: ReactNode }) {
  const { t, language, setLanguage } = useLanguage()
  const session = useSession()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [stored, setMode] = useWorkMode()
  // A Work view is Work, whatever was stored: a link or an address can open one while Expert is remembered (IP0).
  const onWorkView = isWorkView(pathname)
  const mode: WorkMode = onWorkView ? 'work' : stored
  const [drawer, setDrawer] = useState(false)
  const header = useRef<HTMLElement>(null)
  const menuButton = useRef<HTMLButtonElement>(null)
  const headerHeight = useHeight(header)
  const client = clientOf(pathname)
  const sections = client ? (mode === 'work' ? CLIENT_WORK_NAV : CLIENT_EXPERT_NAV) : PORTFOLIO_NAV
  const counts = useNavCounts(client, pathname)

  useEffect(() => {
    if (onWorkView && stored !== 'work') setMode('work')
  }, [onWorkView, stored, setMode])

  useEffect(() => {
    if (!drawer) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setDrawer(false)
      menuButton.current?.focus()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [drawer])

  const switchMode = (m: WorkMode) => {
    setMode(m)
    if (client) navigate(equivalentPath(pathname, m))
  }

  return (
    <div className="flex min-h-full flex-col">
      <a href="#main" className="sr-only focus:not-sr-only">
        {t('shell.skip')}
      </a>
      <header ref={header} className="sticky top-0 z-20 border-b border-ol-border bg-white">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2 sm:px-6">
          <button
            ref={menuButton}
            type="button"
            className="rounded p-1 lg:hidden"
            aria-label={drawer ? t('shell.closeMenu') : t('shell.openMenu')}
            aria-expanded={drawer}
            onClick={() => setDrawer((d) => !d)}
          >
            {drawer ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          </button>
          <Link to="/portfolio" className="flex items-center gap-2">
            <Mark size={28} label={t('app.name')} />
            <span className="text-lg font-semibold tracking-tight text-ol-text">{t('app.name')}</span>
          </Link>
          <ClientSwitcher />
          <div className="ml-auto flex flex-wrap items-center gap-3">
            {client && (
              <div role="group" aria-label={t('shell.mode.label')} className="flex overflow-hidden rounded-md border border-ol-border">
                {(['work', 'expert'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    aria-pressed={mode === m}
                    onClick={() => switchMode(m)}
                    className={`px-3 py-1 text-[15px] ${mode === m ? 'bg-ol-action text-white' : 'bg-white text-ol-text hover:bg-ol-page'}`}
                  >
                    {t(m === 'work' ? 'shell.mode.work' : 'shell.mode.expert')}
                  </button>
                ))}
              </div>
            )}
            <div role="group" aria-label={t('shell.language')} className="flex overflow-hidden rounded-md border border-ol-border">
              {LANGUAGES.map((l) => (
                <button
                  key={l.value}
                  type="button"
                  lang={l.value}
                  aria-pressed={language === l.value}
                  title={l.label}
                  onClick={() => setLanguage(l.value)}
                  className={`px-2 py-1 text-[15px] uppercase ${language === l.value ? 'bg-ol-text text-white' : 'bg-white text-ol-text hover:bg-ol-page'}`}
                >
                  {l.value}
                </button>
              ))}
            </div>
            <span className="hidden text-sm text-ol-text-2 sm:inline">{t('shell.signedInAs', { name: session.person.displayName })}</span>
            <button type="button" onClick={() => void session.signOut()} className="text-sm text-ol-text-2 underline hover:text-ol-action-dark">
              {t('shell.signOut')}
            </button>
          </div>
        </div>
      </header>
      <div className="flex flex-1">
        <aside
          // At phone width the header wraps to two rows in a client (the Work/Expert switch), so the drawer's top is
          // the header's measured height, not a fixed one, or the header would cover the rail's first items.
          style={drawer ? { top: headerHeight } : undefined}
          className={`${drawer ? 'fixed inset-x-0 bottom-0 z-10 block overflow-y-auto bg-white' : 'hidden'} w-full shrink-0 border-r border-ol-border bg-white lg:static lg:block lg:w-64`}
        >
          <Rail sections={sections} client={client} counts={counts} onNavigate={() => setDrawer(false)} />
        </aside>
        <main id="main" className="min-w-0 flex-1">
          {children}
        </main>
      </div>
      <footer className="border-t border-ol-border bg-white">
        <div className="flex flex-wrap gap-x-4 gap-y-1 px-4 py-2 text-sm text-ol-text-2 sm:px-6">
          <Link to="/about" className="underline hover:text-ol-action-dark">
            {t('shell.about')}
          </Link>
          <Link to="/about/method" className="underline hover:text-ol-action-dark">
            {t('shell.method')}
          </Link>
          <Link to="/privacy" className="underline hover:text-ol-action-dark">
            {t('shell.privacy')}
          </Link>
        </div>
      </footer>
      {/* Ask Olivia (H3b-6): on every signed-in screen; it reads the guide only, never a client. */}
      <AskOlivia />
    </div>
  )
}
